import type { Count, MonthlyStack } from "@server/reporting/metrics";
import { percent } from "@server/reporting/metrics";

// Charts are drawn as plain SVG so the report pages need no chart library. Text takes its colour from the page theme.

const PALETTE = ["#216e8a", "#00c7a1", "#e59b5c", "#8c3859", "#4d88a1", "#ffcd00", "#286964", "#f2aac6", "#0a6cff", "#7aa8b9"];
const GREY = "#b7c2c0";

const colourFor = (label: string, index: number): string =>
  label === "Others" || label === "(none)" ? GREY : (PALETTE[index % PALETTE.length] ?? GREY);

const number = (value: number, places = 2): string => value.toFixed(places).replace(/\.?0+$/, "");

// A round number at or above the value, so axis ticks are tidy.
export function niceMax(value: number): number {
  if (value <= 0) {
    return 1;
  }
  const power = 10 ** Math.floor(Math.log10(value));
  const fraction = value / power;
  const step = [1, 2, 3, 4, 6, 8, 10].find((candidate) => fraction <= candidate) ?? 10;
  return step * power;
}

const TICKS = 4;

function YAxis({ max, left, top, height, width }: { max: number; left: number; top: number; height: number; width: number }) {
  return (
    <g className="chart-axis">
      {Array.from({ length: TICKS + 1 }, (_, i) => {
        const y = top + height - (height * i) / TICKS;
        return (
          <g key={i}>
            <line x1={left} x2={left + width} y1={y} y2={y} className="chart-grid" />
            <text x={left - 6} y={y + 3} textAnchor="end">
              {number((max * i) / TICKS, 1)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export function Legend({ items }: { items: { label: string; detail?: string; colour: string; onSelect?: () => void }[] }) {
  return (
    <ul className="chart-legend">
      {items.map((item) => (
        <li key={item.label}>
          <span className="legend-swatch" style={{ background: item.colour }} />
          {item.onSelect ? (
            <button type="button" className="legend-button" onClick={item.onSelect} title="See these tickets">
              {item.label}
            </button>
          ) : (
            <span>{item.label}</span>
          )}
          {item.detail && <span className="legend-detail">{item.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

// With `onSelect`, a slice or its legend entry opens the tickets behind it. "Others" is a mix, so it cannot be opened.
export function PieChart({ data, title, unit = "ticket", onSelect }: { data: Count[]; title: string; unit?: string; onSelect?: (label: string) => void }) {
  const total = data.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) {
    return <p className="record-note">Nothing to show for these filters.</p>;
  }
  const centre = 100;
  const radius = 90;
  let angle = -Math.PI / 2;
  const slices = data.map((item, index) => {
    const share = item.count / total;
    const start = angle;
    angle += share * 2 * Math.PI;
    const point = (at: number, r: number): [number, number] => [centre + r * Math.cos(at), centre + r * Math.sin(at)];
    const [x1, y1] = point(start, radius);
    const [x2, y2] = point(angle, radius);
    const middle = point((start + angle) / 2, radius * 0.62);
    return {
      item,
      share,
      colour: colourFor(item.label, index),
      // A single slice is a whole circle, which one arc cannot draw.
      path: share >= 0.9999 ? undefined : `M ${centre} ${centre} L ${x1} ${y1} A ${radius} ${radius} 0 ${share > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`,
      middle,
    };
  });
  const summary = data.slice(0, 5).map((item) => `${item.label} ${percent(item.count, total)}`).join(", ");
  return (
    <div className="chart-pie">
      <svg viewBox="0 0 200 200" role="img" aria-label={`${title}: ${summary}`} className="chart">
        {slices.map(({ item, colour, path }) => {
          const open = onSelect && item.label !== "Others" ? () => onSelect(item.label) : undefined;
          const link = open ? { onClick: open, role: "link", tabIndex: 0, className: "pie-slice-link", "aria-label": `${item.label}: see these tickets`, onKeyDown: (event: React.KeyboardEvent) => (event.key === "Enter" || event.key === " ") && open() } : {};
          return path ? <path key={item.label} d={path} fill={colour} stroke="var(--surface)" strokeWidth="1" {...link} /> : <circle key={item.label} cx={centre} cy={centre} r={radius} fill={colour} {...link} />;
        })}
        {slices.map(({ item, share, middle }) =>
          share >= 0.04 ? (
            <text key={item.label} x={middle[0]} y={middle[1] + 3} textAnchor="middle" className="chart-pie-label">
              {(share * 100).toFixed(1)}%
            </text>
          ) : null,
        )}
      </svg>
      <Legend
        items={slices.map(({ item, share, colour }) => ({
          label: item.label,
          detail: `${item.count} ${item.count === 1 ? unit : `${unit}s`} (${(share * 100).toFixed(1)}%)`,
          colour,
          onSelect: onSelect && item.label !== "Others" ? () => onSelect(item.label) : undefined,
        }))}
      />
    </div>
  );
}

// A half-circle dial from 0 to `max`. A tick marks `target`.
export function Gauge({ value, max = 10, target, caption, unit = "days" }: { value?: number; max?: number; target?: number; caption: string; unit?: string }) {
  const point = (fraction: number, r: number): [number, number] => [100 - r * Math.cos(fraction * Math.PI), 100 - r * Math.sin(fraction * Math.PI)];
  const fraction = value === undefined ? 0 : Math.min(1, Math.max(0, value / max));
  const [endX, endY] = point(fraction, 80);
  const [tickInX, tickInY] = target === undefined ? [0, 0] : point(target / max, 68);
  const [tickOutX, tickOutY] = target === undefined ? [0, 0] : point(target / max, 92);
  return (
    <figure className="gauge">
      <svg viewBox="0 0 200 124" role="img" aria-label={`${caption}: ${value === undefined ? "no data" : `${number(value)} ${unit}`}`} className="chart">
        <path d="M 20 100 A 80 80 0 0 1 180 100" className="gauge-track" />
        {fraction > 0 && <path d={`M 20 100 A 80 80 0 0 1 ${endX} ${endY}`} className="gauge-fill" />}
        {target !== undefined && <line x1={tickInX} y1={tickInY} x2={tickOutX} y2={tickOutY} className="gauge-tick" />}
        <text x="100" y="96" textAnchor="middle" className="gauge-value">
          {value === undefined ? "–" : number(value)}
        </text>
        <text x="20" y="118" textAnchor="middle" className="chart-axis-text">
          0
        </text>
        <text x="180" y="118" textAnchor="middle" className="chart-axis-text">
          {max}
        </text>
      </svg>
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

const W = 640;
const H = 280;
const M = { left: 44, right: 28, top: 24, bottom: 44 };

export function BarChart({ bars, target, targetLabel, title }: { bars: { label: string; value?: number }[]; target?: number; targetLabel?: string; title: string }) {
  const max = niceMax(Math.max(target ?? 0, ...bars.map((bar) => bar.value ?? 0)));
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const slot = plotW / Math.max(1, bars.length);
  const y = (value: number): number => M.top + plotH - (value / max) * plotH;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${bars.map((bar) => `${bar.label} ${bar.value === undefined ? "none" : number(bar.value)}`).join(", ")}`} className="chart">
      <YAxis max={max} left={M.left} top={M.top} height={plotH} width={plotW} />
      {bars.map((bar, index) => {
        const x = M.left + index * slot + slot * 0.2;
        const barW = slot * 0.6;
        return (
          <g key={bar.label}>
            {bar.value !== undefined && <rect x={x} y={y(bar.value)} width={barW} height={M.top + plotH - y(bar.value)} fill="var(--chart-main)" rx="2" />}
            {bar.value !== undefined && (
              <text x={x + barW / 2} y={y(bar.value) - 5} textAnchor="middle" className="chart-value">
                {number(bar.value)}
              </text>
            )}
            <text x={x + barW / 2} y={H - M.bottom + 18} textAnchor="middle" className="chart-axis-text">
              {bar.label}
            </text>
          </g>
        );
      })}
      {target !== undefined && (
        <g>
          <line x1={M.left} x2={M.left + plotW} y1={y(target)} y2={y(target)} className="chart-target" />
          {targetLabel && (
            <text x={M.left + plotW} y={y(target) - 5} textAnchor="end" className="chart-target-label">
              {targetLabel}
            </text>
          )}
        </g>
      )}
    </svg>
  );
}

export function LineChart({ points, title }: { points: { label: string; value?: number }[]; title: string }) {
  const max = niceMax(Math.max(0, ...points.map((point) => point.value ?? 0)));
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const step = points.length > 1 ? plotW / (points.length - 1) : 0;
  const x = (index: number): number => M.left + (points.length > 1 ? index * step : plotW / 2);
  const y = (value: number): number => M.top + plotH - (value / max) * plotH;
  // A month with no figure breaks the line instead of being drawn as zero.
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((point, index) => {
    if (point.value === undefined) {
      if (current.length > 0) {
        segments.push(current.join(" "));
        current = [];
      }
    } else {
      current.push(`${x(index)},${y(point.value)}`);
    }
  });
  if (current.length > 0) {
    segments.push(current.join(" "));
  }
  const every = Math.ceil(points.length / 8);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${points.map((point) => `${point.label} ${point.value === undefined ? "none" : number(point.value)}`).join(", ")}`} className="chart">
      <YAxis max={max} left={M.left} top={M.top} height={plotH} width={plotW} />
      {segments.map((segment) => (
        <polyline key={segment} points={segment} className="chart-line" />
      ))}
      {points.map((point, index) => (
        <g key={point.label}>
          {point.value !== undefined && <circle cx={x(index)} cy={y(point.value)} r="3.5" className="chart-dot" />}
          {point.value !== undefined && (
            <text x={x(index)} y={y(point.value) - 9} textAnchor="middle" className="chart-value">
              {number(point.value)}
            </text>
          )}
          {index % every === 0 && (
            <text x={x(index)} y={H - M.bottom + 18} textAnchor="middle" className="chart-axis-text">
              {point.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export function StackedBars({ stack, title }: { stack: MonthlyStack; title: string }) {
  const totals = stack.counts.map((counts) => counts.reduce((sum, count) => sum + count, 0));
  const max = niceMax(Math.max(0, ...totals));
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const slot = plotW / Math.max(1, stack.months.length);
  const every = Math.ceil(stack.months.length / 8);
  if (stack.months.length === 0) {
    return <p className="record-note">Nothing to show for these filters.</p>;
  }
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${stack.months.map((month, i) => `${month.label} ${totals[i] ?? 0}`).join(", ")}`} className="chart">
        <YAxis max={max} left={M.left} top={M.top} height={plotH} width={plotW} />
        {stack.months.map((month, monthIndex) => {
          let used = 0;
          const x = M.left + monthIndex * slot + slot * 0.15;
          return (
            <g key={month.key}>
              {stack.labels.map((label, labelIndex) => {
                const count = stack.counts[monthIndex]?.[labelIndex] ?? 0;
                if (count === 0) {
                  return null;
                }
                const height = (count / max) * plotH;
                const top = M.top + plotH - ((used + count) / max) * plotH;
                used += count;
                return (
                  <rect key={label} x={x} y={top} width={slot * 0.7} height={height} fill={colourFor(label, labelIndex)}>
                    <title>{`${month.label}, ${label}: ${count}`}</title>
                  </rect>
                );
              })}
              {monthIndex % every === 0 && (
                <text x={x + (slot * 0.7) / 2} y={H - M.bottom + 18} textAnchor="middle" className="chart-axis-text">
                  {month.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Legend items={stack.labels.map((label, index) => ({ label, colour: colourFor(label, index) }))} />
    </div>
  );
}
